import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { PsModalTuner } from 'stencilSetup';
import {
  EnrollmentContext,
  PlanYear,
  useConfigurationQuery,
  useEnrollmentContextQuery,
} from 'theSource/queries';
import { api } from 'theSource/utils';
import { ResetEnrollmentPlanYearProps } from './MainNavigation';
import { FormElement } from 'enrollment/components/Shared/FormElement';
import { formatDateString } from '../EnrollmentBox/utils';
import { ROUTES } from 'theSource/app/routes';

export const ResetEnrollmentModal = () => {
  const [resetEnable, setResetEnable] = useState<boolean>(false);

  const { t } = useTranslation('navigation.additional');

  const navigate = useNavigate();

  const homepageRedirectlink = useSelector(
    (state: any) => state?.authentication?.redirectLink
  );

  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;

  const { enrollmentContext } = useEnrollmentContextQuery({
    enabled: true,
    apiHost: String(lumosApiHost),
  });

  // mirror ps_spyder logic app/client/ps_spyder/enrollment/components/Admin/planYears.js
  const getPlanYearsFromContexts = (
    contexts: EnrollmentContext[] | undefined
  ) => {
    const orgPlanYears: PlanYear[] = [];

    if (!contexts) {
      return [];
    }

    contexts.forEach((context) => {
      context.included_plan_years.forEach((orgPlanYear) => {
        orgPlanYears.push(orgPlanYear);
      });
    });

    return orgPlanYears;
  };

  const getContextTypes = () => {
    return enrollmentContext?.data?.map(
      (context) => context.enrollment_type_lookup_code
    );
  };

  const resetEnrollmentContextTypes = useMemo(
    () => getContextTypes(),
    // eslint-disable-next-line
    [enrollmentContext]
  );

  const getResetSelection = () => {
    const resetData: ResetEnrollmentPlanYearProps[] = [];

    enrollmentContext?.data?.forEach((context) => {
      context.included_plan_years.forEach((planYear) => {
        const selectedElem: HTMLInputElement | null = document.querySelector(
          `input[type=radio][name='${planYear.id}']:checked`
        );
        if (selectedElem) {
          const planYearId = Number(selectedElem?.name);
          const value = selectedElem?.value;
          const currentObj: ResetEnrollmentPlanYearProps = {};
          currentObj.plan_year_id = planYearId;
          currentObj.clear_coverages = value === 'reset' ? false : true;
          resetData.push(currentObj);
        }
      });
    });

    return resetData;
  };

  const confirmResetEnrollment = () => {
    api
      .put(`${lumosApiHost}/v1/self_service/enrollment/reset_election`, {
        enrollment_context_types: resetEnrollmentContextTypes,
        plan_years: getResetSelection(),
      })
      .then(() => {
        undoResetSelection();
        if (homepageRedirectlink === '/') {
          window.location.reload();
        } else {
          navigate(ROUTES.main.home.path);
          window.location.reload();
        }
      })
      .catch((err) => {
        console.log(err?.response?.data?.details);
      });
  };

  const undoResetSelection = () => {
    document
      .querySelectorAll("#reset-enrollment-content input[type='radio']")
      .forEach((input) => {
        (input as HTMLInputElement).checked = false;
      });
    setResetEnable(false);
  };

  const handleResetEnrollmentSelection = (elem: HTMLInputElement) => {
    if (elem.checked) {
      !resetEnable && setResetEnable(true);
    }
  };

  const renderResetConfirmationBody = () => {
    return (
      <div>
        <h3 className="mb-0">{t('admin.resetEnrollment')}:</h3>
        <p>{t('admin.resetEnrollmentText')}</p>
        <h3 className="mb-0 mt-4">{t('admin.resetAndRemoveCoverages')}:</h3>
        <p>{t('admin.resetAndRemoveCoveragesText')}</p>
        <table id="reset-enrollment-content">
          <thead>
            <tr>
              <th className="pt-3">{t('admin.planYearDates')}</th>
              <th className="pt-3">{t('admin.resetEnrollment')}</th>
              <th className="pt-3">{t('admin.resetAndRemove')}</th>
            </tr>
          </thead>
          <tbody>
            {getPlanYearsFromContexts(enrollmentContext?.data)?.map(
              (context, index) => {
                return (
                  <tr key={index}>
                    <td className="pt-3">
                      {formatDateString(context.starts_on.toString())} -{' '}
                      {formatDateString(context.ends_on.toString())}
                    </td>
                    <td className="pt-3 text-center">
                      <FormElement
                        formField="radio"
                        inputId={`reset-enrollment-check-${context.id}`}
                        label={t('admin.resetEnrollment')}
                        name={`${context.id}`}
                        value="reset"
                        onChange={(e) => {
                          const target = e.target as HTMLInputElement;
                          handleResetEnrollmentSelection(target);
                        }}
                      />
                    </td>
                    <td className="pt-3 text-center">
                      <FormElement
                        formField="radio"
                        inputId={`remove-enrollment-check-${context.id}`}
                        label={t('admin.resetAndRemove')}
                        name={`${context.id}`}
                        value="remove"
                        onChange={(e) => {
                          const target = e.target as HTMLInputElement;
                          handleResetEnrollmentSelection(target);
                        }}
                      />
                    </td>
                  </tr>
                );
              }
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <PsModalTuner
      modalId="reset-enrollment-modal"
      modalTitle={t('admin.resetEnrollmentTitle')}
      primaryButton={t('common:reset')}
      primaryActive={true}
      secondaryButton={t('common:cancel')}
      modalSize="large"
      onPsModalDismiss={undoResetSelection}
      onPsModalSubmit={() => confirmResetEnrollment()}
      disablePrimary={!resetEnable}
    >
      {renderResetConfirmationBody()}
    </PsModalTuner>
  );
};
